// src/companies/index.js — екран „Мои фирми" (Фаза 3.2). Пренесен дословно от app.js.

import { el, clear, field, input, row, clickableRow } from '../shared/dom.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import { panelHead, emptyState } from '../shared/ui.js';
import { openModal, closeModal, confirmDialog } from '../shared/modal.js';
import { viesButton } from '../shared/vies.js';
import { state, companyBooks } from '../shared/state.js';
import { suggestLegalForm } from '../shared/legal.js';
import { dateInput } from '../shared/dates.js';
import { forgetCompanyLogo } from '../shared/companyLogo.js';
import ibanLib from '../../services/iban.js';
// Кодовете за Конто имат свой прозорец (Промени 16.10, т. 3г) — тук е само
// бутонът към него.
import { kontoSettingsDialog } from '../shared/kontoSettings.js';
import { kontoSyncDialog, kontoAnalyticsDialog } from './kontoSync.js';
import { kontoJsonDialog } from './kontoJson.js';
import { bulkCheckDialog } from './bulkCheck.js';

let radioSeq = 0;

// Секцията „Склад и Конто" (ПРОМТ-7). Връща елемента и collect() — събира
// стойностите като ВЛОЖЕН обект за PUT. Нарочно НЕ живее в `f` на формата:
// save() обхожда `f` плоско, а modules/konto са обекти.
// Заварена фирма може да няма нито едно от полетата — тогава екранът
// показва подразбиранията на сървъра (routes/companies.js, settingsPatch).
function warehouseKontoSection(c) {
  const mods = (c.modules && typeof c.modules === 'object') ? c.modules : {};
  // Отметките и радио бутоните НЕ са в `.field`: общият стил `.field input`
  // (width:100 %, padding) разпъваше кръгчето на целия ред и текстът отлиташе
  // в другия край на диалога (test.bvkont.com, 25.09). Редът е label.choice —
  // кутийка и текст един до друг, кликаем и по текста; стилът е в CSS-а.
  const check = (on, text) => {
    const box = el('input', { type: 'checkbox', checked: !!on });
    const wrap = el('label', { class: 'choice' }, box, el('span', { text }));
    return { box, wrap };
  };
  // Радио групата е fieldset + legend: четецът казва въпроса преди
  // отговорите. inline = късите групи (по 2 опции) стоят на един ред.
  // extra — елементи, които са ЧАСТ от въпроса (отметката за себестойността
  // е към режима на експорта), затова стоят в същата група.
  const radios = (legend, cur, opts, inline, ...extra) => {
    const name = 'rg-' + (++radioSeq);
    const inputs = opts.map(([v]) => el('input', { type: 'radio', name, value: v, checked: v === cur }));
    const labels = opts.map(([, text], i) => el('label', { class: 'choice' }, inputs[i], el('span', { text })));
    const fs = extra.length
      // Легендата е на fieldset-а; опциите в свой ред, допълнението — под тях.
      ? el('fieldset', { class: 'choice-group' }, el('legend', { text: legend }),
        el('div', { class: 'choice-group' + (inline ? ' inline' : '') }, ...labels), ...extra)
      : el('fieldset', { class: 'choice-group' + (inline ? ' inline' : '') }, el('legend', { text: legend }), ...labels);
    return { fs, inputs, value: () => { const x = inputs.find((i) => i.checked); return x ? x.value : cur; } };
  };
  const mode = radios('Функция на склада', c.warehouse_mode || 'none', [
    ['none', 'Без склад (услуги) — днешното поведение'],
    ['trade', 'Търговия — стоки, доставки, продажби'],
    ['materials', 'Ресторант / производство — материали и ястия']]);
  // Отметка „Склад" няма: сървърът смята modules.warehouse от функцията на
  // склада (routes/companies.js, settingsPatch) — двете не могат да се разминат.
  const mPur = check(mods.purchases === true, 'Доставки');
  const mOss = check(mods.oss === true, 'OSS (продажби на физически лица в ЕС)');
  // Разчитането с AI праща страниците на документа към външен модел — затова
  // е изрично „да" по фирма и заварена фирма без полето остава изключена
  // (ПРОМТ-10б, т. 7). Без него услугата чете само с локалния Tesseract.
  const ocrAi = check(c.ocr_ai === true, 'Разчитане с AI (страниците отиват към модела)');
  // „Фактурите изписват от склада" (ПРОМТ-16): без изрична стойност следва
  // функцията — търговия да, ресторант не. Докато админът не я пипне на ръка,
  // смяната на функцията я нагласява сама; изрично записаната се пази.
  const explicitInv = c.invoice_stock === true || c.invoice_stock === false;
  let invTouched = false;
  const invDefault = (m) => m === 'trade';
  const inv = check(explicitInv ? c.invoice_stock : invDefault(c.warehouse_mode), 'Фактурите изписват от склада');
  inv.box.addEventListener('change', () => { invTouched = true; });
  const invWrap = el('div', { class: 'whk-inv' }, inv.wrap,
    el('p', { class: 'whk-note', text: 'Изключете, когато продажбите идват от касата — иначе продаденото се изписва два пъти.' }));
  const syncInv = () => {
    const m = mode.value();
    invWrap.style.display = (m === 'trade' || m === 'materials') ? '' : 'none';
    if (!invTouched && !explicitInv) inv.box.checked = invDefault(m);
  };
  mode.inputs.forEach((x) => x.addEventListener('change', syncInv));
  syncInv();
  const neg = radios('Отрицателни наличности', c.negative_stock || 'warn', [['warn', 'Предупреждавай'], ['block', 'Забранявай']], true);
  // „Изнасяй и себестойността" и групирането на изписванията (ПРОМТ-33) са в
  // „Настройки за Конто" → „Изписване" — заедно с папката и вида документ на
  // същия файл. Старите полета на фирмата се четат оттам като подразбиране.
  const exp = radios('Режим на експорта към Конто', c.konto_export_mode || 'summary', [['summary', 'Обобщено (както досега)'], ['lines', 'По редове']], true);

  // „Доставки" НЕ зависи от функцията на склада: доставките (и OCR-ът) са за
  // всички фирми. При фирма с услуги редовете на покупката отиват само като
  // разход към Конто, без складово движение.

  // Кодовете за Конто (документи, контрагенти, сделки, плащане, валута,
  // мерки…) са в „Настройки за Конто" — един прозорец за всичко, което
  // файлът пише сам. Тук остава само пътят дотам: две места за едни и същи
  // кодове объркват и едното тихо презаписва другото.
  const kontoBtn = el('button', { class: 'btn sm', type: 'button', text: 'Настройки за Конто…',
    onClick: () => kontoSettingsDialog(c && c.id ? ((state().companies || []).find((x) => String(x.id) === String(c.id)) || c) : null) });

  // Белият списък на агента (24.10.2026): от тези адреси агентът приема
  // документи по пощата. Имейлът на фирмата и клиентските профили се броят
  // и без да са тук — полето е за допълнителните (счетоводител, управител…).
  const agentEmails = el('textarea', { rows: 3, placeholder: 'например: office@firma.bg', value: (Array.isArray(c.agent_emails) ? c.agent_emails : []).join('\n') });
  // Празно — агентът търси папката по името на фирмата в „БВ КОНТ".
  const agentFolder = input({ placeholder: 'https://drive.google.com/drive/folders/…', value: c.agent_drive_folder ? 'https://drive.google.com/drive/folders/' + c.agent_drive_folder : '' });
  const ossOn = check(c.oss_enabled === true, 'Фирмата е регистрирана по OSS');
  const ossFrom = dateInput(c.oss_registered_from || '');
  const ossAcc = input({ value: c.oss_vat_account || '4535', placeholder: '4535' });

  // Класът whk носи правилата само за тази секция (пренасяне на редовете,
  // въздух между групите) — другите секции на формата не се пипат.
  const node = el('div', { class: 'whk' },
    el('p', { class: 'whk-note', text: 'Какво прави фирмата и как се изнасят документите ѝ към Конто. Складът не може да се изключи, докато има движения.' }),
    mode.fs,
    invWrap,
    el('div', { class: 'choice-group' }, el('div', { class: 'lbl', text: 'Включени модули' }), mPur.wrap, ocrAi.wrap, mOss.wrap),
    // Двете къси групи — една до друга, когато ширината стига; иначе
    // редът се пренася (виж .whk .field-row в styles.css).
    row(neg.fs, exp.fs),
    el('h4', { text: 'Кодове за Конто' }),
    el('p', { class: 'whk-note', text: c && c.id
      ? 'Всички кодове и стойности, които отиват във файловете за Конто (документи, контрагенти, сделки, плащане, валута, мерки, изписването от склада), са на едно място — „Настройки за Конто". Отварят се и от Фактури → Действия.'
      : 'Кодовете за Конто се задават след като запазите фирмата — бутонът „Настройки за Конто" е тук и във Фактури → Действия.' }),
    c && c.id ? kontoBtn : null,
    el('h4', { text: 'Автоматичен агент (поща)' }),
    el('p', { class: 'whk-note', text: 'Агентът приема документи само от познати адреси. Имейлът на фирмата и профилите на клиента вече са познати; тук добавете други — по един на ред.' }),
    field('Имейли, от които агентът приема документи', agentEmails),
    field('Папка на фирмата в Google Drive (линк)', agentFolder),
    el('h4', { text: 'OSS' }),
    ossOn.wrap,
    row(field('Регистрирана по OSS от (дата)', ossFrom, { size: 's' }), field('Сметка за ДДС по OSS', ossAcc, { size: 's' })));

  // Връща обекта за payload-а или хвърля с текст за toast.
  const collect = () => {
    // Дата, която не се чете, НЕ се праща като празна — иначе би изтрила
    // записаната без дума.
    if (String(ossFrom.value || '').trim() && !ossFrom.dataset.iso) throw new Error('Датата на OSS регистрацията не е разпозната — въведете я като дд.мм.гггг.');
    return {
      warehouse_mode: mode.value(),
      // warehouse не се праща — сървърът го смята от warehouse_mode.
      modules: { purchases: mPur.box.checked, oss: mOss.box.checked },
      ocr_ai: ocrAi.box.checked,
      negative_stock: neg.value(),
      // Само ако е пипната — иначе остава „по функцията" (или заварената стойност).
      ...(invTouched ? { invoice_stock: inv.box.checked } : {}),
      konto_export_mode: exp.value(),
      // konto НЕ се праща: кодовете се пишат от „Настройки за Конто"
      // (PUT /companies/:id/konto), а сървърът пази непратеното.
      oss_enabled: ossOn.box.checked,
      oss_registered_from: ossFrom.dataset.iso || '',
      oss_vat_account: ossAcc.value.trim() || '4535',
      agent_emails: agentEmails.value,
      agent_drive_folder: agentFolder.value.trim(),
    };
  };
  return { node, collect };
}

export function view(page) {
  clear(page);
  const isAdmin = state().user && state().user.role === 'admin';
  const panel = el('div', { class: 'panel' });
  const head = panelHead('Мои фирми', isAdmin ? 'Нова фирма' : null, isAdmin ? () => companyForm() : null);
  // Контрагентите на всички фирми от една таблица от Конто (ПРОМТ-23) —
  // създава фирми и пипа контактите на всички, затова е само за админ.
  if (isAdmin) head.appendChild(el('button', { class: 'btn sm ghost', type: 'button', text: 'Внос от Конто', onClick: () => kontoSyncDialog() }));
  // Аналитичностите и номенклатурите (ПРОМТ-31) — пак за всички фирми, само админ.
  if (isAdmin) head.appendChild(el('button', { class: 'btn sm ghost', type: 'button', text: 'Аналитичности и номенклатури', onClick: () => kontoJsonDialog({ onOld: kontoAnalyticsDialog }) }));
  // ДДС номерата на контактите от всички фирми във ВИЕС (ПРОМТ-36) — само админ.
  if (isAdmin) head.appendChild(el('button', { class: 'btn sm ghost', type: 'button', text: 'Проверка на контрагентите', onClick: () => bulkCheckDialog() }));
  panel.appendChild(head);
  if (!state().companies.length) panel.appendChild(emptyState('⌂', isAdmin ? 'Добавете първата си фирма, за да започнете.' : 'Няма достъпни фирми за вашия профил.', isAdmin ? '+ Нова фирма' : null, isAdmin ? () => companyForm() : null));
  else {
    const tb = el('tbody');
    // Всеки отваря пълната форма (Промени 16.10, т. 1) — какво вижда вътре
    // решава ролята: клиентът е без „Склад и Конто", „Изтрий" е само за админ.
    state().companies.forEach((c) => tb.appendChild(clickableRow(() => companyForm(c), 'Отвори ' + c.name,
      el('td', {}, el('span', { class: 'menu', style: 'display:inline-block;width:12px;height:12px;border-radius:50%;padding:0;border:0;background:' + (c.color || '#0f766e') })),
      el('td', { style: 'font-weight:600' }, c.name),
      el('td', { class: 'hide-sm' }, c.eik || '—'),
      el('td', { class: 'hide-sm' }, c.city || '—'),
      el('td', {}, c.invoice_prefix ? ('Представка: ' + c.invoice_prefix) : '10-цифрен №')
    )));
    // (В.9) Таблица без заглавен ред — колоните не се разбираха.
    panel.appendChild(el('table', {}, el('thead', {}, el('tr', {},
      el('th', { scope: 'col' }, el('span', { class: 'sr-only', text: 'Цвят' })),
      el('th', { scope: 'col', text: 'Фирма' }),
      el('th', { scope: 'col', class: 'hide-sm', text: 'ЕИК' }),
      el('th', { scope: 'col', class: 'hide-sm', text: 'Град' }),
      el('th', { scope: 'col', text: 'Номерация' }))), tb));
  }
  page.appendChild(panel);
}

// Отваря формата на фирма направо на дадена секция — ползва го обяснението
// „Складът не е включен" (src/shell/shell.js), за да не търси човек къде е
// настройката. За кантората (админ и потребител) — „Склад и Конто" е тяхна.
export function openCompanySettings(companyId, section) {
  const c = (state().companies || []).find((x) => Number(x.id) === Number(companyId));
  if (c) companyForm(c, { openSection: section });
}

function companyForm(existing, opts) {
  const c = existing || {};
  const openSection = (opts && opts.openSection) || '';
  // Кой какво пипа (Промени 16.10, т. 1): всеки с достъп до фирмата
  // редактира данните ѝ; „Склад и Конто" — само кантората (админ и
  // потребител); „Изтрий" — само админ. Сървърът пази същото правило
  // (routes/companies.js, OFFICE_ONLY_KEYS) — тук само не рисуваме излишното.
  const role = state().user && state().user.role;
  const isAdmin = role === 'admin';
  const isStaff = !!role && role !== 'client';
  const whk = isStaff ? warehouseKontoSection(c) : null;
  const colors = ['#0f766e', '#1d4ed8', '#b45309', '#7c3aed', '#be123c', '#0891b2', '#4d7c0f'];
  const colorInput = el('input', { type: 'hidden', value: c.color || '#0f766e' });
  const swatches = el('div', { style: 'display:flex;gap:8px;flex-wrap:wrap' });
  function drawSwatches() {
    clear(swatches);
    colors.forEach((col) => {
      const sel = colorInput.value === col;
      swatches.appendChild(el('button', { style: `width:28px;height:28px;border-radius:50%;border:2px solid ${sel ? 'var(--ink)' : 'transparent'};background:${col}`, onClick: () => { colorInput.value = col; drawSwatches(); } }));
    });
  }
  drawSwatches();
  const f = {
    name: input({ value: c.name || '', placeholder: 'Моята Фирма ЕООД' }),
    eik: input({ value: c.eik || '' }),
    vat_number: input({ value: c.vat_number || '', placeholder: 'BG…' }),
    mol: input({ value: c.mol || '' }),
    city: input({ value: c.city || '' }),
    address: input({ value: c.address || '' }),
    phone: input({ value: c.phone || '' }),
    email: input({ type: 'email', value: c.email || '' }),
    bank: input({ value: c.bank || '' }),
    iban: input({ value: c.iban || '' }),
    invoice_prefix: input({ value: c.invoice_prefix || '', placeholder: 'напр. 2024 (само цифри, по избор)' }),
    // „Заключи фактурите до дата" (invoice_lock_date) е премахнато — фактурите
    // се заключват на ниво документ при експорт към Конто (авг. 2026).
    invoice_due_days: input({ type: 'number', min: '0', max: '365', value: c.invoice_due_days || '', placeholder: 'напр. 14 (празно = 14)' }),
    legal_form: (() => {
      const sug = c.legal_form || suggestLegalForm(c);
      return el('select', {},
        el('option', { value: '', text: '— не е зададена —', selected: !sug }),
        el('option', { value: 'ООД', text: 'ООД', selected: sug === 'ООД' }),
        el('option', { value: 'ЕООД', text: 'ЕООД', selected: sug === 'ЕООД' }),
        el('option', { value: 'друго', text: 'друго', selected: sug === 'друго' }));
    })(),
    // Номерация / броячи — по вид документ (виж секцията долу).
    proforma_start: input({ type: 'number', min: '1', value: c.proforma_start || '', placeholder: 'напр. 1' }),
    proforma_prefix: input({ value: c.proforma_prefix || '', placeholder: 'по избор' }),
    protocol_start: input({ type: 'number', min: '1', value: c.protocol_start || '', placeholder: 'напр. 1' }),
    protocol_prefix: input({ value: c.protocol_prefix || '', placeholder: 'напр. ВОП (по избор)' }),
    dividend_start: input({ type: 'number', min: '1', value: c.dividend_start || '', placeholder: 'напр. 1' }),
    dividend_prefix: input({ value: c.dividend_prefix || '', placeholder: 'по избор' }),
    assembly_start: input({ type: 'number', min: '1', value: c.assembly_start || '', placeholder: 'напр. 1' }),
    waybill_start: input({ type: 'number', min: '1', value: c.waybill_start || '', placeholder: 'напр. 1' }),
    waybill_prefix: input({ value: c.waybill_prefix || '', placeholder: 'по избор' }),
    purchase_seq_start: input({ type: 'number', min: '1', value: c.purchase_seq_start || '', placeholder: 'напр. 1' }),
    purchase_cc_start: input({ type: 'number', min: '1', value: c.purchase_cc_start || '', placeholder: 'напр. 1' }),
    smtp_host: input({ value: c.smtp_host || '', placeholder: 'напр. mail.bvkont.com' }),
    smtp_port: input({ value: c.smtp_port || '', placeholder: 'напр. 465 / 587 / 26' }),
    smtp_user: input({ value: c.smtp_user || '', placeholder: 'имейл за вход' }),
    smtp_from: input({ value: c.smtp_from || '', placeholder: 'подател (по избор)' }),
    smtp_pass: input({ type: 'password', placeholder: c.smtp_pass_set ? 'настроена — празно = без промяна; при нов сървър, порт или потребител въведете я пак' : 'парола на пощата' }),
  };
  // IBAN (Макс, 19.10.2026): проверка с контролното число, докато се пише,
  // и банката сама по кода ѝ в българския IBAN (services/iban.js).
  const _ibanWas = ibanLib.clean(c.iban || '');
  const ibanHint = el('div', { class: 'cal-hint', 'aria-live': 'polite', style: 'padding:0 0 8px' });
  function checkIban(final) {
    const v = ibanLib.clean(f.iban.value);
    f.iban.classList.remove('bad');
    if (!v) { ibanHint.textContent = ''; return true; }
    const r = ibanLib.check(v);
    if (r.ok) {
      ibanHint.textContent = '✓ IBAN-ът е верен' + (r.bank ? ' — ' + r.bank : '') + '.';
      if (r.bank && !f.bank.value.trim()) f.bank.value = r.bank;
      if (final) f.iban.value = ibanLib.pretty(v);
      return true;
    }
    // Докато се пише, не крещим за непълен номер — чак при излизане от полето.
    if (!final && v.length < 22) { ibanHint.textContent = ''; return false; }
    ibanHint.textContent = '✗ ' + r.reason;
    f.iban.classList.add('bad');
    return false;
  }
  f.iban.addEventListener('input', () => checkIban(false));
  f.iban.addEventListener('blur', () => checkIban(true));
  if (c.iban) checkIban(false);
  const smtpSecure = el('input', { type: 'checkbox', checked: !!c.smtp_secure });
  // Портът решава „SSL": 465 — с, 587 и 26 — без (STARTTLS). Сбърканата
  // двойка висеше две минути без обяснение (Макс, 17.10.2026).
  f.smtp_port.addEventListener('change', () => {
    const p = Number(f.smtp_port.value);
    if (p === 465) smtpSecure.checked = true;
    if (p === 587 || p === 26 || p === 25) smtpSecure.checked = false;
  });
  // (Г.5в) Готови профили: честите доставчици с готови сървър/порт/SSL.
  // Потребителят и паролата винаги са си негови.
  const SMTP_PRESETS = [
    { key: '', label: '— изберете доставчик (по избор) —' },
    // ABV не допуска сървъри: писмата тръгват през общата поща от името на
    // фирмата, отговорите — в ABV (mailer.js). Паролата не е нужна.
    { key: 'abv', label: 'ABV.bg', host: 'smtp.abv.bg', port: '465', secure: true },
    // Gmail — 587 (STARTTLS); ABV работи само на 465 (отворен в Hetzner от 17.10.2026).
    { key: 'gmail', label: 'Gmail', host: 'smtp.gmail.com', port: '587', secure: false },
    { key: 'office', label: 'Office 365 / Outlook', host: 'smtp.office365.com', port: '587', secure: false },
    { key: 'superhosting', label: 'SuperHosting', host: 'mail.superhosting.bg', port: '465', secure: true },
  ];
  const smtpPresetSel = el('select', {}, ...SMTP_PRESETS.map((pr) => el('option', { value: pr.key, text: pr.label })));
  smtpPresetSel.addEventListener('change', () => {
    const pr = SMTP_PRESETS.find((x) => x.key === smtpPresetSel.value);
    if (!pr || !pr.host) return;
    f.smtp_host.value = pr.host;
    f.smtp_port.value = pr.port;
    smtpSecure.checked = pr.secure;
    toast('Попълнени са сървърът и портът за ' + pr.label + ' — остават имейлът и паролата.');
  });

  // (Г.5в) Грешките от пощата са жаргон („535 Authentication…") — превод
  // по шаблони; непознатото се показва както си е.
  function smtpErrText(msg) {
    const m = String(msg || '');
    // Готовото обяснение от сървъра (mailer.js: smtpReason) — както е.
    if (/не отговаря|не е намерен|Сигурната връзка|не прие|Gmail иска|отказва връзки/.test(m)) return m;
    if (/535|auth|credential|user|парол/i.test(m)) return 'Пощата не прие потребителя или паролата.';
    if (/ENOTFOUND|EAI_AGAIN|getaddrinfo|not found/i.test(m)) return 'Сървърът не е намерен — проверете адреса (напр. mail.abv.bg).';
    if (/ETIMEDOUT|timeout|ECONNREFUSED|ECONNRESET/i.test(m)) return 'Сървърът не отговаря — проверете адреса и порта.';
    if (/certificate|TLS|SSL/i.test(m)) return 'Проблем със сигурната връзка — пробвайте с изключено/включено SSL и другия порт.';
    return m;
  }
  const smtpTestBtn = el('button', { class: 'btn sm ghost', text: 'Тествай връзката', onClick: async () => {
    if (!existing) { toast('Първо запазете фирмата, после тествайте.', true); return; }
    const orig = smtpTestBtn.textContent; smtpTestBtn.disabled = true; smtpTestBtn.textContent = 'Тествам…';
    try {
      const r = await api('/companies/' + existing.id + '/smtp-test', { method: 'POST', body: JSON.stringify({
        smtp_host: f.smtp_host.value, smtp_port: f.smtp_port.value, smtp_secure: smtpSecure.checked,
        smtp_user: f.smtp_user.value, smtp_from: f.smtp_from.value, smtp_pass: f.smtp_pass.value || undefined,
      }) });
      // ABV не пуска сървъри — писмата тръгват през общата поща от името на
      // фирмата, а отговорите идват в ABV (17.10.2026).
      if (r && r.via_server) toast('Готово ✓ ABV не допуска пращане от сървъри, затова писмата ще тръгват през общата поща от името на фирмата, а отговорите на клиентите ще идват в ' + (r.reply_to || 'ABV пощата') + '.');
      else toast('Връзката е успешна ✓');
    } catch (e) { toast(smtpErrText(e.message), true); }
    finally { smtpTestBtn.disabled = false; smtpTestBtn.textContent = orig; }
  } });
  // Собственици/съдружници. ЕГН тук е пълно (формата е само за админ);
  // в останалата система се показва маскирано. Дяловете са само за справка.
  let ownersData = Array.isArray(c.shareholders) ? c.shareholders.map((s) => ({ name: s.name || '', egn: s.egn || '', share_percent: s.share_percent != null ? String(s.share_percent) : '' })) : [];
  const ownersWrap = el('div');
  function drawOwners() {
    clear(ownersWrap);
    ownersData.forEach((s, i) => {
      const nameI = input({ value: s.name, placeholder: 'Име и фамилия' });
      nameI.addEventListener('input', (e) => { s.name = e.target.value; });
      const egnI = input({ value: s.egn, placeholder: 'ЕГН', maxlength: '10', inputmode: 'numeric' });
      egnI.addEventListener('input', (e) => { s.egn = e.target.value; });
      const pctI = input({ value: s.share_percent, placeholder: '%', type: 'number', min: '0', max: '100', step: '0.01', style: 'max-width:90px' });
      pctI.addEventListener('input', (e) => { s.share_percent = e.target.value; });
      ownersWrap.appendChild(el('div', { class: 'field-row', style: 'align-items:flex-end' },
        field('Собственик', nameI), field('ЕГН', egnI), field('Дял % (справка)', pctI),
        el('button', { class: 'btn sm danger', type: 'button', text: '×', style: 'margin-bottom:2px', onClick: () => { ownersData.splice(i, 1); drawOwners(); } })));
    });
    ownersWrap.appendChild(el('button', { class: 'btn sm ghost', type: 'button', text: '+ Добави собственик', onClick: () => { ownersData.push({ name: '', egn: '', share_percent: '' }); drawOwners(); } }));
  }
  drawOwners();

  let booksData = Array.isArray(c.invoice_books) ? c.invoice_books.map((b) => ({ name: b.name || '', start: String(b.start || '') })) : [];
  const booksWrap = el('div');
  // Вграденият основен брояч (по представката) важи само при ПРАЗЕН списък —
  // Макс (Маятико, 03.11.2026) добави фактурник и „първият изчезна". Затова
  // при празен списък пише кой брояч върви, а първият „+ Добави фактурник"
  // вкарва основния като първи ред, за да не се губи.
  const defaultBook = () => {
    const b = companyBooks({ invoice_prefix: f.invoice_prefix.value })[0];
    return { name: 'Основен', start: String(b.start) };
  };
  function drawBooks() {
    clear(booksWrap);
    if (!booksData.length) {
      booksWrap.appendChild(el('p', { style: 'font-size:13px;color:var(--muted);margin:0 0 8px',
        text: 'Сега фактурите вървят по основния брояч (от № ' + defaultBook().start.padStart(10, '0') + '). Ако добавите фактурник, той ще остане в списъка като „Основен“.' }));
    }
    booksData.forEach((b, i) => {
      const nameI = input({ value: b.name, placeholder: 'Име (напр. Основен)' });
      nameI.addEventListener('input', (e) => { b.name = e.target.value; });
      const startI = input({ value: b.start, placeholder: 'Начален № (напр. 1000000001)' });
      startI.addEventListener('input', (e) => { b.start = e.target.value; });
      booksWrap.appendChild(el('div', { class: 'field-row', style: 'align-items:flex-end' },
        field('Име', nameI), field('Начален №', startI),
        el('button', { class: 'btn sm danger', type: 'button', text: '×', style: 'margin-bottom:2px', onClick: () => { booksData.splice(i, 1); drawBooks(); } })));
    });
    booksWrap.appendChild(el('button', { class: 'btn sm ghost', type: 'button', text: '+ Добави фактурник', onClick: () => { if (!booksData.length) booksData.push(defaultBook()); booksData.push({ name: '', start: '' }); drawBooks(); } }));
  }
  drawBooks();

  // Секция „Номерация / броячи" — всички настройки за номерата на едно място,
  // поотделно за всяка фирма. Началният № важи само за НОВИ документи.
  const numberNote = 'важи само за нови документи; не пренарежда издадените';
  function docRow(label, startInput, prefixInput) {
    const fields = [field('Начален № — ' + numberNote, startInput)];
    if (prefixInput) fields.push(field('Представка (по избор)', prefixInput));
    return el('div', { style: 'margin:8px 0' },
      el('div', { style: 'font-weight:600;margin:0 0 2px', text: label }),
      row(...fields));
  }
  const numberingSection = el('div', {},
    el('h4', { text: 'Броячи по вид документ', style: 'margin:12px 0 4px' }),
    el('p', { style: 'font-size:13px;color:var(--muted);margin:0 0 8px', text: 'Настройките за номерата на документите — поотделно за всяка фирма. Началният номер важи само за НОВИ документи; не пренарежда и не пипа вече издадените. Ако зададете начало под вече използван номер, броячът просто продължава от най-високия.' }),
    el('div', { style: 'font-weight:600;margin:6px 0 2px', text: 'Фактури — фактурници (серии)' }),
    el('p', { style: 'font-size:13px;color:var(--muted);margin:0 0 8px', text: 'По избор. Всеки фактурник е отделна серия с начален номер; при издаване избирате от кой да продължи номерът. Без зададени — един основен 10-цифрен брояч.' }),
    booksWrap,
    docRow('Проформи', f.proforma_start, f.proforma_prefix),
    docRow('Протоколи (ВОП по чл. 117)', f.protocol_start, f.protocol_prefix),
    docRow('Дивиденти', f.dividend_start, f.dividend_prefix),
    docRow('Събрания (протоколи на ОС)', f.assembly_start, null),
    docRow('Пътни листа', f.waybill_start, f.waybill_prefix),
    docRow('Доставки — пореден № (дава се при потвърждаване)', f.purchase_seq_start, null),
    docRow('Доставки — № на счетоводна статия, СС (дава се при експорт към Конто)', f.purchase_cc_start, null),
  );

  // (Г.5б) Сгъваеми секции: човек влиза да смени телефон, не да гледа
  // стена от броячи и SMTP. Отворена е само „Основни данни"; останалите се
  // разгъват при нужда. Задължителното поле е отбелязано със *.
  const sec = (title, isOpen, ...kids) => {
    const d = el('details', { class: 'form-sec' }, el('summary', { text: title }), el('div', { class: 'form-sec-body' }, ...kids));
    if (isOpen) d.setAttribute('open', '');
    return d;
  };
  const body = el('div', {},
    el('p', { class: 'cal-hint', style: 'padding:0 0 8px', text: 'Полетата със * са задължителни. Останалите секции се разгъват с клик.' }),
    sec('Основни данни', true,
      field('Име на фирмата *', f.name),
      row(field('ЕИК / Булстат', f.eik), field('ДДС номер', f.vat_number)),
      el('div', { style: 'margin:-2px 0 10px' }, viesButton(() => f.vat_number.value || f.eik.value, (r) => {
        if (!f.name.value && r.name) f.name.value = r.name;
        if (!f.address.value && r.address) f.address.value = r.address;
        if (!f.vat_number.value) f.vat_number.value = r.countryCode + r.vatNumber;
      })),
      row(field('МОЛ', f.mol), field('Град', f.city)),
      field('Адрес', f.address),
      row(field('Телефон', f.phone), field('Имейл', f.email)),
      field('Правна форма (за протокола по чл. 134 — ООД/ЕООД макет)', f.legal_form),
      field('Цвят на фирмата', swatches), colorInput),
    sec('Банка', false,
      row(field('Банка', f.bank), field('IBAN', f.iban)), ibanHint),
    sec('Номерация', false,
      field('Представка на фактурите (по избор)', f.invoice_prefix),
      field('Срок за плащане на фактурите (дни след издаване)', f.invoice_due_days),
      numberingSection),
    sec('Изпращане по имейл', false,
      el('p', { style: 'font-size:13px;color:var(--muted);margin:0 0 8px', text: 'Тези данни ги дава вашият доставчик на поща — поискайте ги от него. Ако оставите празно, фактурите на тази фирма тръгват от общата поща на сървъра.' }),
      field('Попълни готов профил', smtpPresetSel),
      row(field('SMTP сървър', f.smtp_host), field('Порт', f.smtp_port)),
      row(field('Потребител (имейл)', f.smtp_user), field('Подател (From)', f.smtp_from)),
      field('Парола', f.smtp_pass),
      el('div', { class: 'field' }, el('label', { class: 'choice' }, smtpSecure, el('span', { text: 'Сигурна връзка (SSL) — обикновено за порт 465' }))),
      el('div', { style: 'margin:0 0 6px' }, smtpTestBtn)),
    sec('Собственици', false,
      el('p', { style: 'font-size:13px;color:var(--muted);margin:0 0 8px', text: isAdmin
        ? 'Ползват се от дивидентния калкулатор и протокола. ЕГН е чувствително — навсякъде другаде се показва маскирано. Дялът е само за справка (разпределението на дивидента е ръчно).'
        // Не-админът вижда ЕГН-то скрито (******1234); непипнато, то остава
        // записаното — сървърът го връща по името на собственика.
        : 'Ползват се от дивидентния калкулатор и протокола. ЕГН се показва скрито (последните 4 цифри); ако не го пипате, остава записаното. Дялът е само за справка.' }),
      ownersWrap),
    whk ? sec('Склад и Конто', openSection === 'warehouse', whk.node) : null
  );
  let logoData = '';
  let logoChanged = false;
  const logoStatus = el('span', { style: 'font-size:13px;color:var(--muted)', text: c.logo_set ? 'Има качено лого' : 'Няма качено лого' });
  const logoFile = el('input', { type: 'file', accept: 'image/png,image/jpeg' });
  logoFile.addEventListener('change', () => {
    const file = logoFile.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 360; let w = img.width, h = img.height;
        if (w > max) { h = Math.round(h * max / w); w = max; }
        const cv = document.createElement('canvas'); cv.width = w; cv.height = h;
        cv.getContext('2d').drawImage(img, 0, 0, w, h);
        logoData = cv.toDataURL('image/png'); logoChanged = true;
        logoStatus.textContent = 'Ново лого избрано ✓';
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
  const logoRemove = el('button', { class: 'btn sm ghost', text: 'Премахни', onClick: () => { logoData = ''; logoChanged = true; logoStatus.textContent = 'Логото ще бъде премахнато'; } });
  const logoSave = el('button', { class: 'btn sm', text: 'Запази логото', onClick: async () => {
    if (!existing || !existing.id) { toast('Първо запазете фирмата, после качете лого.', true); return; }
    if (!logoChanged) { toast('Първо изберете или премахнете лого.', true); return; }
    try { await api('/companies/' + existing.id + '/logo', { method: 'POST', body: JSON.stringify({ logo: logoData }) }); toast('Логото е запазено.'); logoChanged = false; forgetCompanyLogo(existing.id); try { state().companies = await api('/companies'); } catch (e) {} }
    catch (e) { toast(e.message, true); }
  } });
  const logoField = field('Лого (PNG/JPG — горе вдясно на фактурата)', el('div', { style: 'display:flex;gap:8px;align-items:center;flex-wrap:wrap' }, logoFile, logoRemove, logoSave, logoStatus));
  body.appendChild(logoField);

  const save = async () => {
    // Сменен IBAN, който не минава проверката, не се записва (заварен грешен —
    // не спира записа на останалото; сървърът прави същото).
    if (ibanLib.clean(f.iban.value) !== _ibanWas && !checkIban(true)) {
      const box = f.iban.closest('details'); if (box) box.open = true;
      toast(ibanHint.textContent.replace(/^✗ /, ''), true); f.iban.focus(); return;
    }
    const p = { color: colorInput.value };
    if (logoChanged) p.logo = logoData;
    for (const k in f) p[k] = ('iso' in f[k].dataset) ? f[k].dataset.iso : f[k].value;
    p.smtp_secure = smtpSecure.checked;
    if (!f.smtp_pass.value) delete p.smtp_pass;
    p.invoice_books = booksData.filter((b) => String(b.start).replace(/\D/g, '')).map((b) => ({ name: (b.name || '').trim() || 'Фактурник', start: String(b.start).replace(/\D/g, '') }));
    p.shareholders = ownersData.filter((s) => (s.name || '').trim()).map((s) => ({ name: s.name.trim(), egn: String(s.egn || '').trim(), share_percent: s.share_percent === '' ? null : Number(s.share_percent) }));
    if (whk) {
      try { Object.assign(p, whk.collect()); }
      catch (e) { toast(e.message, true); return; }
    }
    try {
      let saved;
      if (existing) saved = await api('/companies/' + existing.id, { method: 'PUT', body: JSON.stringify(p) });
      else { saved = await api('/companies', { method: 'POST', body: JSON.stringify(p) }); if (!state().activeCompany) { state().activeCompany = saved.id; localStorage.setItem('activeCompany', saved.id); } }
      state().companies = await api('/companies'); closeModal(); toast('Записано.');
      const warns = (saved && Array.isArray(saved.numbering_warnings)) ? saved.numbering_warnings : [];
      warns.forEach((w) => toast(w, true));
      window.CRM.renderShell();
    } catch (e) { toast(e.message, true); }
  };
  const foot = el('div', {});
  if (existing && isAdmin) foot.appendChild(el('button', { class: 'btn sm danger', text: 'Изтрий', onClick: async () => { if (await confirmDialog('ВНИМАНИЕ: Изтриването на фирма „' + (existing.name || '') + '“ ще изтрие ЗАВИНАГИ и всичко свързано с нея — контакти, сделки, задачи, бележки, фактури, проформи, протоколи, дивиденти и артикули. Това не може да се върне. Сигурни ли сте?', { okText: 'Изтрий', danger: true })) {
    // Проваленото ИЗТРИВАНЕ спира дотук с ясно съобщение. Провал само на
    // опресняването после НЕ бива да излъже „не се получи" — изтрито Е,
    // затова списъкът се чисти локално като резервен вариант.
    try { await api('/companies/' + existing.id, { method: 'DELETE' }); }
    catch (e) { toast('Изтриването не се получи. ' + e.message, true); return; }
    if (state().activeCompany === existing.id) { state().activeCompany = null; localStorage.removeItem('activeCompany'); }
    try { state().companies = await api('/companies'); }
    catch (e) { state().companies = (state().companies || []).filter((c) => c.id !== existing.id); }
    closeModal(); toast('Фирмата и всички свързани данни са изтрити.'); window.CRM.renderShell();
  } }, style: 'margin-right:auto' }));
  foot.appendChild(el('button', { class: 'btn ghost', text: 'Отказ', onClick: closeModal }));
  foot.appendChild(el('button', { class: 'btn', text: 'Запази', onClick: save }));
  openModal({ title: existing ? 'Редакция на фирма' : 'Нова фирма', body, foot, wide: true });
}

